import React, { useEffect, useMemo, useRef } from 'react';
import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/system/styleFunctionSx';
import type { CatalogItem } from '@jucy-ui/common';
import { FleetCategoryTeaserCard } from '@jucy-ui/components';
import { FleetCategoryTeaserCardActionsProps } from '@jucy-ui/components/FleetCategory/components/TeaserCard/components';
import { BlockContainer } from '@/components/Blocks/BlockContainer';
import { BlockTitle } from '@/components/Blocks/components/BlockTitle';
import { ResponsiveSlider } from '@/components/Blocks/components/ResponsiveSlider';
import { FleetCategoryCard } from '@/components/Cards/FleetCategoryCard';
import { useSiteLinkPrefix } from '@/hooks/useSiteLinkPrefix';
import analytics from '@/services/analytics';
import { CmsFleetTypeCardsBlock } from '@/services/cms/blocks/CmsFleetTypeCardsBlock';

export interface FleetTypeCardsBlockProps extends CmsFleetTypeCardsBlock {
    sx?: SxProps<Theme>;
}

export const FleetTypeCardsBlock = ({ sx, ...props }: FleetTypeCardsBlockProps) => {
    const fleetCategories = useMemo(() => props.fleetCategories?.filter((category) => Boolean(category.gallery?.length)) || [], [props.fleetCategories]);
    const count = fleetCategories.length || 0;
    const placeHolders = Array.from({ length: count < 3 ? 3 - count : 0 }, (_, i) => i);

    const hasTriggeredEvent = useRef(false);
    useEffect(() => {
        if (!hasTriggeredEvent.current && fleetCategories.length) {
            analytics.plugins.ecommerce.trackEcommerce('view_item_list', {
                item_list_id: `FleetTypeCardsBlock_${props.id}`,
                item_list_name: props.title,
                items: fleetCategories.map((fleetCategory) => ({
                    item_id: fleetCategory.code,
                    item_name: fleetCategory.name,
                    item_brand: fleetCategory.fleetTypeSlug === 'motorhome' ? 'star' : 'jucy',
                    item_category: `${props.fleetTypeSlug}-${props.regionCode}`,
                })),
            });
            hasTriggeredEvent.current = true;
        }
    }, [props.id, props.title, props.fleetTypeSlug, props.regionCode, fleetCategories]);

    return (
        <BlockContainer sx={sx} {...props}>
            <BlockTitle {...props} />
            {props.layoutStyle === 'Columns' ? (
                <ResponsiveSlider>
                    {fleetCategories.map((category) => (
                        <FleetCategoryCard
                            key={category.code}
                            fleetCategory={category}
                            onClick={() => {
                                analytics.plugins.interactions.trackInteraction({
                                    source: 'Fleet category card',
                                    action: 'click',
                                    label: category.name,
                                });
                                analytics.plugins.ecommerce.trackEcommerce('select_item', {
                                    item_list_id: `FleetTypeCardsBlock_${props.id}`,
                                    item_list_name: props.title,
                                    items: [
                                        {
                                            item_id: category.code,
                                            item_name: category.name,
                                            item_brand: category.fleetTypeSlug === 'motorhome' ? 'star' : 'jucy',
                                            item_category: `${category.fleetTypeSlug}-${props.regionCode}`,
                                        },
                                    ],
                                });
                            }}
                        />
                    ))}
                    {placeHolders.map((i) => (
                        <div key={i} />
                    ))}
                </ResponsiveSlider>
            ) : (
                <Stack
                    sx={{
                        gap: 2,
                    }}
                >
                    {fleetCategories
                        .filter((category) => Boolean(category.gallery?.length))
                        ?.map((category) => (
                            <FleetCategoryTeaserCard
                                onClick={() => {
                                    analytics.plugins.interactions.trackInteraction({
                                        source: 'Fleet category card',
                                        action: 'click',
                                        label: category.name,
                                    });
                                    analytics.plugins.ecommerce.trackEcommerce('select_item', {
                                        item_list_id: `FleetTypeCardsBlock_${props.id}`,
                                        item_list_name: props.title,
                                        items: [
                                            {
                                                item_id: category.code,
                                                item_name: category.name,
                                                item_brand: category.fleetTypeSlug === 'motorhome' ? 'star' : 'jucy',
                                                item_category: `${category.fleetTypeSlug}-${props.regionCode}`,
                                            },
                                        ],
                                    });
                                }}
                                key={category.code}
                                slotProps={{
                                    descriptionPanel: {
                                        slotProps: {
                                            detailsPrimaryText: {
                                                sx: {
                                                    lineHeight: '1.5',
                                                    fontWeight: '700',
                                                    fontSize: '18px',
                                                },
                                            },
                                            detailsSecondaryText: {
                                                sx: {
                                                    margin: '0px',
                                                    fontWeight: '400',
                                                    lineHeight: '1.66',
                                                    color: 'rgba(0, 0, 0, 0.6)',
                                                    fontSize: '14px',
                                                },
                                            },
                                        },
                                    },
                                }}
                                fleetCategory={{
                                    ...category,
                                    categoryCode: category.code,
                                    type: '',
                                    rentalDays: 0,
                                    fleetTypeCode: props.fleetTypeSlug || '',
                                    availabilityMessage: '',
                                    dailyRate: { value: 0, currencyCode: '' },
                                    total: { value: 0, currencyCode: '' },
                                    availability: 'StopSell',
                                }}
                                slots={{
                                    actionsPanel: FleetCategoryCardRowActions,
                                }}
                                catalogItem={category as CatalogItem}
                            />
                        ))}
                </Stack>
            )}
        </BlockContainer>
    );
};

const FleetCategoryCardRowActions = React.forwardRef<HTMLDivElement, FleetCategoryTeaserCardActionsProps>(({ ownerState }, ref) => {
    const addSiteLinkPrefix = useSiteLinkPrefix();
    return (
        <Box ref={ref} sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <Button variant="contained" href={addSiteLinkPrefix(ownerState.catalogItem.pages[0].url)}>
                View details
            </Button>
        </Box>
    );
});
FleetCategoryCardRowActions.displayName = 'FleetCategoryCardRowActions';
